/**
 * Auth store using Zustand.
 * Manages authentication state and reacts to shared client session expiry.
 */

import { create } from 'zustand';
import { authService } from '../services/auth';
import type { TelegramLoginData, TelegramOidcCallbackData, User } from '../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username?: string) => Promise<void>;
  telegramLogin: (data: TelegramLoginData) => Promise<void>;
  telegramOidcLogin: (data: TelegramOidcCallbackData) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  clearError: () => void;
  initialize: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(email, password);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  register: async (email: string, password: string, username?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.register(email, password, username);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  telegramLogin: async (data: TelegramLoginData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.telegramLogin(data);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Telegram login failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  telegramOidcLogin: async (data: TelegramOidcCallbackData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.telegramOidcLogin(data);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Telegram OIDC login failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    await authService.logout();
    set({ user: null, isAuthenticated: false });
  },

  setUser: (user: User) => set({ user }),

  clearError: () => set({ error: null }),

  initialize: () => {
    if (authService.isAuthenticated()) {
      authService.startAutoRefresh();
      set({ isAuthenticated: true });
    }
  },
}));

if (typeof window !== 'undefined') {
  window.addEventListener('tarifdata:session-expired', () => {
    authService.stopAutoRefresh();
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: 'Session expired',
    });
  });
}
